import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

import { AGGRESSIVENESS, GRANULARITY } from '../constants';

const br = (value: number, digits = 2) => value.toFixed(digits).replace('.', ',');

interface HelpModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export function HelpModal({ open, onOpenChange }: HelpModalProps) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Como funciona a estrutura de preço</DialogTitle>
                    <DialogDescription>
                        O motor lê o desempenho real de cada segmento no GAM e monta a escada de
                        preços da cascata. Um setup por domínio.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-6 text-sm text-gray-700">
                    <Section title="O que o motor faz">
                        <p>
                            Para cada segmento — combinação de <Code>bloco × país × post</Code> — ele
                            busca o eCPM e o PMR dos últimos dias, decide onde a cascata começa e
                            termina, distribui os steps entre esses dois extremos e encaixa cada
                            valor num dos 172 preços que existem no GAM.
                        </p>
                        <Flow />
                    </Section>

                    <Section title="Agressividade">
                        <p>
                            Define <strong>onde a cascata vive</strong>. O topo é um múltiplo do eCPM
                            do segmento; o piso é uma fração do topo. O alvo de PMR é o quanto de
                            entrega aquele nível se propõe a sustentar.
                        </p>
                        <table className="w-full text-xs">
                            <thead className="text-left text-gray-500">
                                <tr>
                                    <th className="py-1 font-medium">Nível</th>
                                    <th className="py-1 font-medium">Topo</th>
                                    <th className="py-1 font-medium">Banda</th>
                                    <th className="py-1 font-medium">Alvo de PMR</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {AGGRESSIVENESS.map((level) => (
                                    <tr key={level.value}>
                                        <td className="py-1">{level.label}</td>
                                        <td className="py-1 font-mono">{br(level.topMultiplier)}×</td>
                                        <td className="py-1 font-mono">{br(level.band)}</td>
                                        <td className="py-1 font-mono">{level.pmrTarget}%</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <Note>
                            O PMR observado corrige o topo automaticamente. Se o segmento está
                            entregando abaixo do alvo, a cascata inteira desce — o que reduz quantos
                            steps ficam acima do eCPM. É um mecanismo só, com dois efeitos.
                        </Note>
                    </Section>

                    <Section title="Granularidade">
                        <p>
                            Define <strong>como os steps se distribuem</strong> dentro da faixa. Os
                            saltos sempre crescem da base para o topo — finos perto do eCPM, onde a
                            compra de fato acontece, e largos em cima, onde se força o preço. A
                            granularidade controla o tamanho do salto na base, como fração do salto
                            médio.
                        </p>
                        <ul className="space-y-1 text-xs">
                            {GRANULARITY.map((level) => (
                                <li key={level.value} className="flex gap-2">
                                    <span className="font-medium min-w-[150px]">{level.label}</span>
                                    <span className="text-gray-500">{level.detail}</span>
                                </li>
                            ))}
                        </ul>
                        <Note>
                            O nível 5 é o limite natural: espaçamento uniforme. Não existe "mais
                            afastado que uniforme" sem inverter a curva. Para abrir mais, mude a
                            agressividade ou a quantidade de steps.
                        </Note>
                    </Section>

                    <Section title="Segmentação">
                        <p>
                            Escolhe a dimensão que separa os segmentos, e é escolha única.{' '}
                            <strong>Post do WordPress</strong> agrupa por <Code>id_post_wp</Code>;{' '}
                            <strong>Campanha</strong> agrupa por <Code>utm_campaign</Code>. Em ambos
                            os casos país e bloco continuam sendo dimensões separadas — o mesmo post
                            na França e na Polônia gera cascatas diferentes, porque os eCPMs
                            divergem.
                        </p>
                    </Section>

                    <Section title="Mínimo de impressões">
                        <p>
                            Filtro duro: segmento abaixo desse volume não gera cascata. Não é
                            burocracia — abaixo de algumas centenas de impressões o PMR fica
                            saturado em 100% e deixa de ser sinal, virando ruído de cauda longa.
                        </p>
                    </Section>

                    <Section title="Janela">
                        <p>
                            Campo duplo: define o <strong>período analisado</strong> e a{' '}
                            <strong>frequência de execução</strong> ao mesmo tempo.
                        </p>
                        <ul className="space-y-1 text-xs">
                            <li>
                                <strong>Ontem</strong> — analisa o dia anterior, roda todo dia de
                                madrugada
                            </li>
                            <li>
                                <strong>3 dias</strong> — analisa os últimos 3 dias, roda a cada 3
                                dias
                            </li>
                            <li>
                                <strong>7 dias</strong> — analisa os últimos 7 dias, roda a cada 7
                                dias
                            </li>
                        </ul>
                        <Note>
                            Janelas maiores dão métricas mais estáveis mas reagem mais devagar a
                            mudanças de comportamento do site.
                        </Note>
                    </Section>

                    <Section title="Steps">
                        <p>
                            Quantos preços a cascata deve ter. É um <strong>pedido, não uma
                            garantia</strong>: os preços válidos no GAM são 172 valores fixos, com
                            resolução irregular. Numa faixa estreita ou num eCPM baixo, simplesmente
                            não existem tantos preços distintos.
                        </p>
                        <Note>
                            Exemplo real: um segmento com eCPM 3,81 tem faixa de 1,88 a 4,00, onde só
                            existem cinco preços. Pedir 20 steps ali entrega 5. O motor sempre
                            informa o que entregou e por quê — nunca corta em silêncio.
                        </Note>
                        <p>
                            <strong>Piso estendido.</strong> Quando a banda da agressividade fica tão
                            estreita que não cabem nem quatro preços, o motor <strong>baixa o
                            piso</strong> em vez de desistir do segmento. O raciocínio: sem
                            granulação, o bloco fica com os steps padrão do sistema, que ignoram o
                            desempenho dele por completo — uma cascata curta porém ancorada no eCPM
                            real é melhor que isso. A extensão aparece declarada no bloco, com o
                            piso original e o novo.
                        </p>
                    </Section>

                    <Section title="PMR alvo">
                        <p>
                            O campo já vem preenchido com o alvo da agressividade escolhida e
                            acompanha as trocas dela. Digitar outro valor cria uma customização —
                            a célula fica destacada em azul e só nesse caso o valor é enviado no
                            setup. Voltar ao número original desfaz a customização.
                        </p>
                    </Section>

                    <Section title="Simulação">
                        <p>
                            Roda o cálculo completo contra o GAM real, <strong>sem gravar nada</strong>.
                            É como calibrar sem esperar o ciclo automático. Cada domínio leva cerca
                            de 8 segundos, até 6 em paralelo.
                        </p>
                        <p>
                            A nota vem de <strong>dois eixos independentes</strong>, e é sempre a
                            pior dos dois. O modal de inspeção mostra qual deles puxou para baixo e
                            contra qual limiar.
                        </p>
                        <table className="w-full text-xs">
                            <thead className="text-left text-gray-500">
                                <tr>
                                    <th className="py-1 font-medium">Eixo</th>
                                    <th className="py-1 font-medium">Saudável</th>
                                    <th className="py-1 font-medium">Atenção</th>
                                    <th className="py-1 font-medium">Ruim</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                <tr>
                                    <td className="py-1">Entrega de steps</td>
                                    <td className="py-1 font-mono">≥ 80%</td>
                                    <td className="py-1 font-mono">≥ 50%</td>
                                    <td className="py-1 font-mono">&lt; 50%</td>
                                </tr>
                                <tr>
                                    <td className="py-1">Cobertura dos segmentos</td>
                                    <td className="py-1 font-mono">≥ 95%</td>
                                    <td className="py-1 font-mono">≥ 85%</td>
                                    <td className="py-1 font-mono">&lt; 85%</td>
                                </tr>
                            </tbody>
                        </table>
                        <Note>
                            <strong>Entrega</strong> é a média de steps gerados sobre os pedidos —
                            mede se o vocabulário do GAM comporta a cascata que você pediu.{' '}
                            <strong>Cobertura</strong> é quantos segmentos chegaram a gerar
                            granulação — mede se os dials fazem sentido para o perfil do domínio.
                        </Note>
                        <Note>
                            Mudar qualquer dial invalida a simulação daquela linha. Simule de novo
                            antes de salvar.
                        </Note>
                    </Section>

                    <Section title="Fronteira do serviço">
                        <p>
                            Este motor <strong>produz</strong> as granulações. Ele não escreve no
                            GAM, não altera as cascatas em produção e não decide como elas são
                            aplicadas no navegador — isso é responsabilidade do serviço consumidor.
                        </p>
                    </Section>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <section className="space-y-2">
            <h3 className="font-semibold text-gray-900">{title}</h3>
            {children}
        </section>
    );
}

function Note({ children }: { children: React.ReactNode }) {
    return (
        <div className="rounded-lg border-l-2 border-blue-300 bg-blue-50/60 px-3 py-2 text-xs text-gray-600">
            {children}
        </div>
    );
}

function Code({ children }: { children: React.ReactNode }) {
    return (
        <code className="rounded bg-gray-100 px-1 py-0.5 font-mono text-xs text-gray-800">
            {children}
        </code>
    );
}


function Flow() {
    return (
        <div className="rounded-lg bg-gray-50 p-3 font-mono text-[11px] leading-relaxed text-gray-600">
            <div>
                <span className="text-gray-400">âncoras</span>{' '}
                topo = eCPM × mult(agressividade) × ajuste(PMR)
            </div>
            <div className="pl-[4.5rem]">piso = topo × banda(agressividade)</div>
            <div className="mt-1">
                <span className="text-gray-400">curva</span>{' '}
                salto na base = salto médio × fator(granularidade)
            </div>
            <div className="mt-1">
                <span className="text-gray-400">encaixe</span>{' '}
                cada preço vai para o valor válido mais próximo
            </div>
        </div>
    );
}
